import React, { ReactNode } from "react";

import {
  IgAzurePrimitives,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { Skeleton, Stack, useTheme } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";

const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isAutoHeight",
})<{ isAutoHeight?: boolean }>(({ theme, isAutoHeight }) => ({
  [`& .ag-theme-alpine`]: {
    "--ag-row-hover-color": IgGrayPrimitives[200],
  },
  [`& .ag-theme-alpine .ag-cell`]: {
    lineHeight: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  [`.ag-body-vertical-scroll`]: {
    width: "0 !important",
    minWidth: "0 !important",
  },
  ".ag-body-vertical-scroll-viewport": {
    right: "6px",
  },
  "& ::-webkit-scrollbar": {
    width: "5px",
  },
  "& .ag-aria-description-container": {
    display: "none",
  },
  "& .ag-body-viewport ": {
    overscrollBehavior: isAutoHeight ? "auto" : "contain",
  },
  [`ag-body-vertical-scroll-viewport`]: {
    right: "6px",
  },
  // The grid frame is an overlay ring, not a border on `.ag-root-wrapper`:
  // AG Grid's header and row backgrounds are descendants of that element and
  // paint over the antialiased inner half of a border, washing out the rounded
  // corners. The ring sits on `.ag-root-wrapper` itself, not on `.ag-root`,
  // because the status bar and the pagination panel are siblings of `.ag-root`
  // and belong inside the frame. `--ig-grid-frame-radius` /
  // `--ig-grid-frame-color` are the knobs call sites use to reshape or remove
  // the frame.
  "--ig-grid-frame-radius": "8px",
  "--ig-grid-frame-color": theme.igBorders.core.default,
  [`& .ag-root-wrapper`]: {
    border: "none !important",
    borderRadius: "var(--ig-grid-frame-radius)",
    // AG Grid sets these too; stated here because the ring depends on them.
    position: "relative",
    overflow: "hidden",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  [`& .ag-root-wrapper::after`]: {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    // Above the header and row backgrounds, below grid popups (z-index 5).
    zIndex: 1,
    borderRadius: "var(--ig-grid-frame-radius)",
    border: "solid 1px var(--ig-grid-frame-color)",
  },
  [`.ag-full-width-row .ag-cell-wrapper.ag-row-group`]: {
    paddingRight: "10px !important",
  },
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
  },
  [`& .ag-header-cell-text`]: {
    color: theme.palette.text.primary,
    fontWeight: "600",
  },
  [`& .ag-theme-alpine .ag-cell-inline-editing`]: {
    height: `50px`,
  },
  [`& .ag-header-row-column-group`]: {
    backgroundColor: IgAzurePrimitives[100],
  },
  [`& .ag-header-row:not(:first-of-type) .ag-header-cell`]: {
    borderTop: "solid 0px !important",
  },
  [`& .ag-header-group-text`]: {
    fontWeight: 500,
  },
  [`& .ag-header-group-cell-label`]: {
    justifyContent: "center",
  },
  [`& .ag-group-value`]: {
    width: "100%",
  },
  [`& .ag-header-icon`]: {
    color: theme.igIconColors.light,
  },
  [`& .ag-sort-indicator-icon`]: {
    color: theme.igIconColors.light,
  },
  [`& .ag-icon-filter`]: {
    color: theme.igIconColors.light,
  },
  [`& .ag-header-cell-resize:after`]: {
    // backgroundColor: theme.igIconColors.light,
  },
  [`& .ag-popup-child:not(.ag-tooltip-custom)`]: {
    // backgroundColor: theme.igIconColors.light,
    borderRadius: "10px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  [`& .ag-header-cell-label`]: {
    justifyContent: "center",
    fontWeight: 400,
    marginRight: "-16px",
    fontSize: "14px",
  },
  // for the exp table alignment
  [`& .header-custom`]: {
    "& .ag-header-cell-label": {
      justifyContent: "left !important",
    },
  },
  [`& .header-custom2`]: {
    "& .ag-header-cell-label": {
      marginRight: "0px !important",
    },
  },
  [`& .performance-header`]: {
    "& .ag-header-cell-label": {
      fontSize: "14px",
    },
  },
  borderRadius: "var(--ig-grid-frame-radius)",
}));

type IgTableWrapperVariant =
  | "default"
  | "simpleList"
  | "settingsCard"
  | "settingsCardCentered"
  | "autoHeight"
  | "autoHeightDefault"
  | "autoHeightMedium"
  | "autoHeightNoMin"
  | "autoHeightTransparentHover"
  | "fullHeight"
  | "flexFullHeight";

interface IgTableWrapperProps {
  variant?: IgTableWrapperVariant;
  children: ReactNode;
  isLoading?: boolean;
  minHeight?: string | null;
  height?: string | null;
  maxHeight?: string;
  sx?: SxProps<Theme>;
}

export const IgTableWrapper = ({
  children,
  height,
  variant = "default",
  isLoading,
  minHeight,
  maxHeight,
  sx,
}: IgTableWrapperProps) => {
  const theme = useTheme();

  const simpleListSx: SxProps = {
    borderRadius: 0,
    "& .ag-theme-alpine": {
      width: "100%",
    },
    "--ig-grid-frame-color": "transparent",
    "--ig-grid-frame-radius": "0",
    "& .ag-header": {
      display: "none",
    },
    "& .ag-row": {
      borderColor: `${theme.igBorders.core.disabled} !important`,
    },
    "& .ag-row-hover": {
      backgroundColor: `${theme.igBackgroundColors.interactive.neutral.hover} !important`,
    },
    "& .ag-cell": {
      border: "0 !important",
      display: "flex",
      justifyContent: "flex-start !important",
      px: "16px !important",
    },
  };

  const settingsCardSx: SxProps<Theme> = {
    "& .ag-header-cell-label": {
      justifyContent: "flex-start !important",
      marginRight: "0 !important",
    },
    "& .ag-header-row": {
      backgroundColor: (theme) => theme.igBackgroundColors.baseLightLow,
    },
    "& .ag-header": {
      borderBottom: "none !important",
    },
    "& .ag-header-cell": {
      borderRight: "none !important",
    },
    "& .ag-cell": {
      borderRight: "none !important",
    },
    "& .ag-row": {
      borderBottom: "none !important",
    },
    "--ig-grid-frame-color": "transparent",
  };

  const settingsCardCenteredSx: SxProps<Theme> = {
    "& .ag-header-cell-centered .ag-header-cell-label": {
      justifyContent: "center !important",
    },
  };

  const transparentHoverSx: SxProps<Theme> = {
    width: "100%",
    "& .ag-row-hover, & .ag-row-hover .ag-cell": {
      backgroundColor: "transparent",
    },
  };

  const variantSx: Partial<Record<IgTableWrapperVariant, SxProps<Theme>>> = {
    simpleList: simpleListSx,
    settingsCard: settingsCardSx,
    settingsCardCentered: [settingsCardSx, settingsCardCenteredSx],
    autoHeightTransparentHover: transparentHoverSx,
    fullHeight: { height: "100%" },
    flexFullHeight: { flex: 1, minHeight: 0 },
  };

  const variantDimensions: Partial<
    Record<
      IgTableWrapperVariant,
      Pick<IgTableWrapperProps, "height" | "minHeight">
    >
  > = {
    settingsCard: {
      height: null,
      minHeight: "20px",
    },
    settingsCardCentered: {
      height: null,
      minHeight: "20px",
    },
    autoHeight: {
      height: null,
      minHeight: "100px",
    },
    autoHeightDefault: {
      height: null,
    },
    autoHeightMedium: {
      height: null,
      minHeight: "120px",
    },
    autoHeightNoMin: {
      height: null,
      minHeight: null,
    },
    autoHeightTransparentHover: {
      height: null,
      minHeight: "120px",
    },
    fullHeight: {
      height: "100%",
      minHeight: null,
    },
    flexFullHeight: {
      height: "100%",
      minHeight: "120px",
    },
  };

  const resolvedHeight =
    height === undefined ? variantDimensions[variant]?.height : height;
  const resolvedMinHeight =
    minHeight === undefined ? variantDimensions[variant]?.minHeight : minHeight;
  const baseSx = variantSx[variant] ?? {};
  const combinedSx: SxProps<Theme> = Array.isArray(sx)
    ? [baseSx, ...sx]
    : [baseSx, sx];

  if (variant) {
    return (
      <StyledStack
        direction={"column"}
        sx={combinedSx}
        isAutoHeight={resolvedHeight === null}
      >
        {isLoading ? (
          <Skeleton
            variant={"rectangular"}
            height={
              resolvedHeight === null ? undefined : resolvedHeight || "500px"
            }
            sx={maxHeight ? { maxHeight } : undefined}
          />
        ) : (
          <div
            className="ag-theme-alpine"
            style={{
              minHeight:
                resolvedMinHeight === null
                  ? undefined
                  : resolvedMinHeight || "500px",
              height:
                resolvedHeight === null ? undefined : resolvedHeight || "500px",
              maxHeight,
            }}
          >
            {children}
          </div>
        )}
      </StyledStack>
    );
  }
};
